/**
 * Human-readable labels for MCP tool names.
 * Falls back to auto-formatting (snake_case → Title Case) for unlisted tools.
 */
const TOOL_LABELS = {
  // Contacts
  search_contacts: 'Searching contacts',
  get_contact: 'Looking up contact',
  create_contact: 'Creating contact',
  update_contact: 'Updating contact',
  delete_contact: 'Deleting contact',

  // Conversations
  list_conversations: 'Listing conversations',
  get_conversation: 'Loading conversation',
  search_conversations: 'Searching conversations',
  send_message: 'Sending message',
  assign_conversation: 'Assigning conversation',
  resolve_conversation: 'Resolving conversation',

  // Events / Planning
  list_events: 'Listing events',
  get_event: 'Loading event details',
  get_event_details: 'Loading event details',
  create_event: 'Creating event',
  'planning.create_event': 'Creating event',
  create_venue_inquiry: 'Creating venue inquiry',
  update_event: 'Updating event',
  delete_event: 'Deleting event',
  find_available_slots: 'Finding available slots',
  check_availability: 'Checking availability',
  'planning.check_availability': 'Checking availability',
  'planning.find_available_slots': 'Finding available slots',

  // Resources / Spaces
  list_resources: 'Listing resources',
  list_spaces: 'Listing spaces',
  get_resource: 'Loading resource',

  // Allocations
  list_allocations: 'Listing allocations',
  create_allocation: 'Creating allocation',
  update_allocation: 'Updating allocation',

  // Knowledge Base
  search_articles: 'Searching articles',
  get_article: 'Loading article',
  create_article: 'Creating article',
  update_article: 'Updating article',

  // Pricing
  list_pricing_plans: 'Listing pricing plans',
  get_pricing_breakdown: 'Calculating price',
  calculate_price: 'Calculating price',

  // Agents / Teams / Managers
  list_ai_agents: 'Listing AI agents',
  list_managers: 'Listing managers',
  list_teams: 'Listing teams',

  // Labels
  list_labels: 'Listing labels',
  create_label: 'Creating label',

  // Canned Responses
  list_canned_responses: 'Listing templates',
  search_canned_responses: 'Searching templates',

  // Analytics
  get_conversation_stats: 'Loading conversation stats',
  get_response_time_metrics: 'Loading response time metrics',
  get_topic_distribution: 'Loading topic distribution',
  get_language_distribution: 'Loading language distribution',
  get_escalation_metrics: 'Loading escalation metrics',
  list_conversations_by_period: 'Listing conversations by period',
  get_conversation_messages: 'Loading conversation messages',
  search_conversation_content: 'Searching conversation content',

  // UI Tools
  show_conversation: 'Opening conversation',
  show_event_sheet: 'Opening event sheet',
  'planning.show_event_ui': 'Opening event',
  'planning.show_event_card': 'Showing event card',
  'pricing.show_pricing_plan': 'Opening pricing plan',
  'planning.show_quote_ui': 'Opening quote preview',
  'booking_widget.show_ui': 'Opening booking widget',
  booking_widget_show_ui: 'Opening booking widget',
  'booking_widget.list': 'Listing booking widgets',
  booking_widget_list: 'Listing booking widgets',
  'booking_widget.get': 'Loading booking widget',
  booking_widget_get: 'Loading booking widget',
  'booking_widget.create': 'Creating booking widget',
  booking_widget_create: 'Creating booking widget',
  'booking_widget.update': 'Updating booking widget',
  booking_widget_update: 'Updating booking widget',
  'booking_widget.destroy': 'Deleting booking widget',
  booking_widget_destroy: 'Deleting booking widget',
  booking_widget_create_locale_copy: 'Creating locale copy',
  booking_widget_funnel: 'Loading booking funnel',
  booking_widget_ssl_status: 'Checking SSL status',
  booking_widget_send_domain_instructions: 'Sending domain instructions',
  create_quote: 'Creating quote',
  get_quote: 'Loading quote',
  update_quote: 'Updating quote',
  revise_quote: 'Revising quote',

  // CSAT
  list_csat_surveys: 'Loading surveys',

  // Inboxes
  list_inboxes: 'Listing inboxes',
};

function resolveToolLabelKey(name) {
  if (!name) return '';
  if (TOOL_LABELS[name]) return name;
  // MCP tools are often namespaced: planning.create_event → create_event
  const bare = name.includes('.') ? name.split('.').pop() : name;
  if (bare && TOOL_LABELS[bare]) return bare;
  return name;
}

function progressiveLabel(name) {
  const key = resolveToolLabelKey(name);
  if (TOOL_LABELS[key]) return TOOL_LABELS[key];
  // Fallback: convert snake_case / dotted names to "Title case"
  const words = key.replace(/\./g, ' ').replace(/_/g, ' ').trim();
  return words.replace(/^\w/, c => c.toUpperCase());
}

/**
 * Get a human-readable label for a tool name.
 * @param {string} name - Tool name (e.g. 'search_contacts')
 * @returns {string} Human-readable label
 */
export function getToolLabel(name) {
  if (!name) return 'Working';
  return progressiveLabel(name);
}

/**
 * Get a past-tense label for completed tools.
 * @param {string} name - Tool name
 * @returns {string}
 */
export function getToolCompletedLabel(name) {
  if (!name) return 'Done';
  const label = progressiveLabel(name);
  if (!label || label === 'Working') {
    return name.replace(/[._]/g, ' ').replace(/^\w/, c => c.toUpperCase());
  }
  // Convert "Searching contacts" → "Searched contacts"
  return label
    .replace(/^Searching/, 'Searched')
    .replace(/^Looking up/, 'Found')
    .replace(/^Loading/, 'Loaded')
    .replace(/^Listing/, 'Listed')
    .replace(/^Creating/, 'Created')
    .replace(/^Updating/, 'Updated')
    .replace(/^Deleting/, 'Deleted')
    .replace(/^Sending/, 'Sent')
    .replace(/^Assigning/, 'Assigned')
    .replace(/^Resolving/, 'Resolved')
    .replace(/^Finding/, 'Found')
    .replace(/^Checking/, 'Checked')
    .replace(/^Calculating/, 'Calculated')
    .replace(/^Opening/, 'Opened')
    .replace(/^Showing/, 'Showed')
    .replace(/^Getting/, 'Got')
    .replace(/^Fetching/, 'Fetched');
}

/**
 * Get a human-readable tool name in gerund/progressive form.
 * Uses TOOL_LABELS if available, otherwise converts snake_case and
 * transforms common verb prefixes to gerund (get → Getting, list → Listing, etc.).
 * @param {string} name - Tool name (e.g. 'get_conversation_stats')
 * @returns {string}
 */
export function getToolHumanName(name) {
  if (!name) return 'Tool';
  const key = resolveToolLabelKey(name);
  if (TOOL_LABELS[key]) return TOOL_LABELS[key];

  // Convert snake_case / dotted names to space-separated
  const words = key.replace(/\./g, ' ').replace(/_/g, ' ').trim();

  // Convert common verb prefixes to gerund form
  const gerundMap = {
    get: 'Getting',
    list: 'Listing',
    search: 'Searching',
    create: 'Creating',
    update: 'Updating',
    delete: 'Deleting',
    send: 'Sending',
    find: 'Finding',
    check: 'Checking',
    show: 'Showing',
    calculate: 'Calculating',
    load: 'Loading',
    fetch: 'Fetching',
    resolve: 'Resolving',
    assign: 'Assigning',
  };

  const firstWord = words.split(' ')[0].toLowerCase();
  const rest = words.split(' ').slice(1).join(' ');

  if (gerundMap[firstWord]) {
    return `${gerundMap[firstWord]}${rest ? ' ' + rest : ''}`;
  }

  return words.replace(/^\w/, c => c.toUpperCase());
}

const FINISHED_STATUSES = new Set([
  'finished',
  'complete',
  'completed',
  'done',
  'success',
  'succeeded',
]);

/**
 * Label for a tool call, using past tense when the call has finished.
 * @param {string} name
 * @param {string} [status]
 * @returns {string}
 */
export function getToolStatusLabel(name, status) {
  if (FINISHED_STATUSES.has(status)) return getToolCompletedLabel(name);
  return getToolHumanName(name);
}
